import { isEmpty, omit } from 'lodash/fp';

import { ROUND_ID_KEY, YEAR_KEY } from '@portal/common/models/data-key';
import {
    ChartType,
    DataCollectionResource,
    DataCondition,
    DataFilters,
    DataTableDisplayState,
    DataToolConfig,
    DataType,
    Organization,
} from '@portal/common/types';

import { DataQuerySource } from '../../models/analytics-source';
import { getMergedDataCollectionGranularity } from '../data-loader';
import { getNowcastsStartYear } from '../forecast-data-helpers';

const ROUTER_STATE_SELECTION_KEYS = [
    'dataCollectionId',
    'conditions',
    'refinementFilters',
    'dataType',
    'chartType',
    'activeRefinements',
    'configuration',
];

export type DataExplorerRouteState = {
    dataLoaded?: boolean;
    dataCollectionId?: number;
    conditions?: DataCondition[];
    refinementFilters?: DataFilters | null;
    dataType?: DataType;
    chartType?: ChartType;
    activeRefinements?: string[];
    configuration?: Partial<DataTableDisplayState> & {
        includeNowcasts?: boolean;
        chartSettings?: {
            areVisible?: boolean;
        };
    };
};

type BuildDataExplorerInitializationPlanOptions = {
    dataCollections: DataCollectionResource[] | null;
    dataToolConfig: DataToolConfig;
    selectedDataCollection: DataCollectionResource | null | undefined;
    dataTool: string;
    organization: Organization | null | undefined;
    routerState: DataExplorerRouteState | null | undefined;
};

export type DataExplorerInitializationPlan = {
    dataTool: string;
    dataCollectionId: number;
    dataType: DataType;
    chartType: ChartType;
    conditions: DataCondition[];
    refinementFilters: DataFilters | null;
    yearRefinementFilterMode?: 'slider' | 'dropdown';
    visibleRefinementFilters?: string[];
    includeNowcasts?: boolean;
    tableDisplayState?: Partial<DataTableDisplayState>;
    chartsVisible?: boolean;
    shouldQuery: boolean;
    querySource?: DataQuerySource;
    cleanedRouterState?: object;
};

export const getYearRefinementFilterMode = (
    filters: DataFilters | null | undefined
): 'slider' | 'dropdown' | undefined => {
    const years = filters?.[YEAR_KEY];

    if (
        !Array.isArray(years) ||
        years.length < 2 ||
        !years.every((year): year is number => typeof year === 'number' && Number.isFinite(year))
    ) {
        return undefined;
    }

    const sortedYears = [...new Set(years)].sort((a, b) => a - b);
    const minYear = sortedYears[0];
    const isContiguousRange = sortedYears.every((year, index) => year === minYear + index);

    return isContiguousRange ? 'slider' : 'dropdown';
};

export const filterRefinementFiltersForDisabledNowcasts = ({
    refinementFilters,
    dataCollections,
    dataCollectionId,
    conditions,
    areNowcastsAllowed,
}: {
    refinementFilters: DataFilters | null | undefined;
    dataCollections: DataCollectionResource[] | null;
    dataCollectionId: number | null | undefined;
    conditions: DataCondition[] | null | undefined;
    areNowcastsAllowed: boolean;
}): DataFilters | null | undefined => {
    if (!refinementFilters || areNowcastsAllowed || !refinementFilters[YEAR_KEY]) {
        return refinementFilters;
    }

    const dataCollection = (dataCollections || []).find(
        (collection) => collection.id === dataCollectionId
    );
    if (!dataCollection || isEmpty(conditions)) {
        return refinementFilters;
    }

    const dataTypes = conditions.map((condition) => condition.data_type);
    const dataset = dataCollection.datasets.find((item) => dataTypes.includes(item.data_type));
    if (!dataset?.forecast_data_calculation_map) {
        return refinementFilters;
    }

    const roundId = refinementFilters[ROUND_ID_KEY];
    if (!roundId) {
        return refinementFilters;
    }

    const forecastDataCalculationMap = dataset.forecast_data_calculation_map[String(roundId)];
    if (!forecastDataCalculationMap) {
        return refinementFilters;
    }

    const nowcastStartYear = getNowcastsStartYear(forecastDataCalculationMap);
    if (nowcastStartYear === null) {
        return refinementFilters;
    }

    const yearFilter = refinementFilters[YEAR_KEY];
    if (!Array.isArray(yearFilter)) {
        return refinementFilters;
    }

    const filteredYears = yearFilter.filter(
        (year): year is number => typeof year === 'number' && year < nowcastStartYear
    );
    if (filteredYears.length === yearFilter.length) {
        return refinementFilters;
    }

    if (isEmpty(filteredYears)) {
        return omit([YEAR_KEY], refinementFilters);
    }

    return {
        ...refinementFilters,
        [YEAR_KEY]: filteredYears,
    };
};

export const buildDataExplorerInitializationPlan = ({
    dataCollections,
    dataToolConfig,
    selectedDataCollection,
    dataTool,
    organization,
    routerState,
}: BuildDataExplorerInitializationPlanOptions): DataExplorerInitializationPlan | null => {
    const initData: DataExplorerRouteState = {};
    const isDataSelectedAndRequestRequired = !!routerState?.dataLoaded;
    let cleanedRouterState: object | undefined;

    if (isDataSelectedAndRequestRequired && routerState) {
        const {
            dataCollectionId,
            conditions,
            refinementFilters,
            dataType,
            chartType,
            activeRefinements,
            configuration,
        } = routerState;

        Object.assign(initData, {
            dataCollectionId,
            conditions,
            refinementFilters,
            dataType,
            chartType,
            activeRefinements,
            configuration,
        });

        cleanedRouterState = omit(ROUTER_STATE_SELECTION_KEYS, routerState);
    }

    const dataProjectsDataCollections = (dataCollections || []).filter((collection) =>
        dataToolConfig.dataProjectIds.includes(collection.data_project_id)
    );

    const { shouldRequestAllDataOnInit } = dataToolConfig;
    if (shouldRequestAllDataOnInit) {
        const dataCollection = dataProjectsDataCollections[0];
        if (!dataCollection) {
            return null;
        }

        const dataTypes = dataCollection.datasets.map(({ data_type }) => data_type);
        const granularity = getMergedDataCollectionGranularity(dataCollection, dataTypes);

        initData.conditions = dataCollection.datasets
            .map((dataset) => {
                const { data_type, granularity, primary_entity_id_filter } = dataset;
                return (granularity[primary_entity_id_filter] || []).map((primary_entity_id) => ({
                    data_type,
                    primary_entity_id,
                }));
            })
            .flat();
        initData.dataCollectionId = dataCollection?.id;
        initData.dataType = dataTypes[0];
        initData.refinementFilters = granularity;
    }

    const { refinementFilters, dataType, chartType, activeRefinements, configuration } = initData;
    let { conditions = [] } = initData;
    let { dataCollectionId } = initData;

    if (dataCollectionId == null) {
        if (selectedDataCollection != null) {
            dataCollectionId = selectedDataCollection.id;
        } else if (!isEmpty(dataProjectsDataCollections)) {
            dataCollectionId = dataProjectsDataCollections[0].id;
        } else {
            return null;
        }
    }

    if (isEmpty(conditions)) {
        conditions = dataToolConfig.defaultConditions ?? [];
    }

    const areNowcastsAllowed = !!organization?.forecast_data_calculation_is_allowed;
    const filteredRefinementFilters = filterRefinementFiltersForDisabledNowcasts({
        refinementFilters,
        dataCollections,
        dataCollectionId,
        conditions,
        areNowcastsAllowed,
    });

    const includeNowcasts = configuration?.includeNowcasts;
    const chartsVisible = configuration?.chartSettings?.areVisible;

    return {
        dataTool,
        dataCollectionId,
        dataType: dataType || dataToolConfig.defaultDataType,
        chartType: chartType || dataToolConfig.defaultChartType,
        conditions,
        refinementFilters: filteredRefinementFilters || null,
        yearRefinementFilterMode: getYearRefinementFilterMode(filteredRefinementFilters),
        visibleRefinementFilters:
            activeRefinements || dataToolConfig.permanentlyVisibleRefinementFilters,
        includeNowcasts:
            typeof includeNowcasts !== 'undefined'
                ? areNowcastsAllowed && includeNowcasts
                : undefined,
        tableDisplayState: configuration,
        chartsVisible: typeof chartsVisible !== 'undefined' ? chartsVisible : undefined,
        shouldQuery: isDataSelectedAndRequestRequired || !!shouldRequestAllDataOnInit,
        cleanedRouterState,
    };
};
